import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiService } from './api.service';
import { UserService } from './user.service';

/**
 * Service responsible for handling user switching workflows, including fetching switchable users, retrieving user services,
 * generating and verifying OTPs, and invalidating OTPs via authenticated API calls.
 * @class
 * @injectable
 * @providedIn root
 *
 * @remarks
 * This service depends on {@link UserService} to validate access tokens and generate request HMAC signatures,
 * and on {@link HttpClient} to communicate with the backend API.
 *
 * @example
 * // Inject and use the service in a component or another service
 * constructor(private userSwitcherService: UserSwitcherService) {}
 *
 * this.userSwitcherService.switcherUsersList(params).subscribe(response => {
 *   console.log(response.body);
 * });
 *
 * @method switcherUsersList
 * Retrieves the list of users available for switching.
 * @param params - Request payload sent to the backend.
 * @returns An observable of the full HTTP response containing the switcher users list.
 *
 * @method userServices
 * Retrieves services available for the current user.
 * @param params - Request payload sent to the backend.
 * @returns An observable of the full HTTP response containing user services.
 *
 * @method generateOTPUserSwitcher
 * Requests generation of an OTP for user switching.
 * @param params - Request payload sent to the backend.
 * @returns An observable of the full HTTP response containing OTP generation details.
 *
 * @method verifyOTP
 * Verifies an OTP for login or user switching.
 * @param params - Request payload sent to the backend.
 * @returns An observable of the full HTTP response containing OTP verification result.
 *
 * @method invalidateOTP
 * Invalidates an OTP associated with user switching.
 * @param params - Request payload sent to the backend.
 * @returns An observable of the full HTTP response indicating whether OTP invalidation succeeded.
 *
 * @lifecycle
 * This injectable service does not implement Angular lifecycle hooks.
 */
@Injectable({
  providedIn: 'root',
})
export class UserSwitcherService {

  constructor(private api: ApiService) { }

  /**
  * Retrieves the list of switcher users by sending an authenticated POST request with the given parameters.
  * @example
  * witcherUsersList(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload used to generate the HMAC and fetch the switcher users list.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response containing the switcher users list.
  **/
  switcherUsersList(params: any): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/user-switcher/switcher-users-list`;
    return this.api.post(url, params);
  }

  /**
   * Sends a request to retrieve the current user's services using a validated bearer token and HMAC-signed headers.
   * @example
   * serServices(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload used to generate the HMAC and sent in the POST body.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the user services data.
   */
  userServices(params: any): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL_V2}/user-services/get-user-services`;
    return this.api.post(url, params);
  }

  /**
   * Generates a one-time password for user switching and sends the request to the server.
   * @example
   * generateOTPUserSwitcher(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload containing the data required to generate the OTP for user switching.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the OTP generation request.
   */
  generateOTPUserSwitcher(params: any): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/user-switcher/generate-otp-user-switcher`;
    return this.api.post(url, params);
  }

  /**
  * Verifies the one-time password (OTP) for user login and returns the HTTP response.
  * @example
  * erifyOTP(params)
  * HttpResponse<any>
  * @param {any} params - OTP verification payload to send in the request body.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the OTP verification request.
  **/
  verifyOTP(params: any): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/login/verifyotp`;
    return this.api.post(url, params);
  }

  /**
   * Invalidates a previously generated OTP for user switching.
   * @example
   * nvalidateOTP({ otp: '123456', userId: 'abc123' })
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload containing the OTP invalidation details.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the invalidate-OTP request.
   */
  invalidateOTP(params: any): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/user-switcher/invalidate-otp`;
    return this.api.post(url, params);
  }
}
